import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"
import {
  getCustomerCompanies,
  getCustomerCompany,
  createCustomerCompany,
  editCustomerCompany,
  deleteCustomerCompany,
  getAllCustomerCompanies,
  getTrashedCustomerCompanies,
  restoreCustomerCompany,
} from "api/customerCompanyApi"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const CUSTOMER_COMPANY_QUERY_KEY = "customer-companies"
export const CUSTOMER_COMPANY_GROUP_QUERY_KEY = "customer-companies-group"

// -------------------- FETCH PAGINATED --------------------
export const useCustomerCompanies = (params = {}, enabled = true) => {
  return usePaginatedResource(
    CUSTOMER_COMPANY_QUERY_KEY,
    getCustomerCompanies,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useCustomerCompany = (id, enabled = true) => {
  return useResourceItem(
    CUSTOMER_COMPANY_QUERY_KEY,
    getCustomerCompany,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateCustomerCompany = () => {
  return useCreateResource(createCustomerCompany, CUSTOMER_COMPANY_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditCustomerCompany = () => {
  return useEditResource(editCustomerCompany, CUSTOMER_COMPANY_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteCustomerCompany = () => {
  return useDeleteResource(deleteCustomerCompany, CUSTOMER_COMPANY_QUERY_KEY)
}

// -------------------- ADDITIONAL --------------------

// Get all customer companies without pagination
export const useAllCustomerCompanies = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_COMPANY_QUERY_KEY,
    getAllCustomerCompanies,
    { ...params, all: true },
    true
  )
}

// Get trashed customer companies
export const useTrashedCustomerCompanies = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_COMPANY_QUERY_KEY,
    getTrashedCustomerCompanies,
    params,
    true
  )
}

// Restore a trashed customer company
export const useRestoreCustomerCompany = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCustomerCompany,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Update paginated cache
      queryClient.setQueriesData([CUSTOMER_COMPANY_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual item cache
      queryClient.setQueryData([CUSTOMER_COMPANY_QUERY_KEY, id], restoredItem)
    },
  })
}
